import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { initializeFirebase, getFirebaseAuthAsync, firebaseManager, isFirebaseInitialized, UserRole, accessControlService, USER_ROLES } from '@watchlist/shared';
import { firebaseService } from '../services/firebaseService';
import { isCapacitorNative, getAuthTimeout } from '../utils/platformUtils';
import { nativeFirebase, UnifiedUser } from '../services/nativeFirebaseService';
import { initializePushNotifications, unregisterPushNotifications } from '../services/pushNotificationService';
import { cleanupStaleDrafts } from '../utils/draftCleanup';
import { clearPersistedProfiles } from '../utils/profilePersistence';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isRoleLoading: boolean;
  isFirebaseReady: boolean;
  userRole: UserRole | null;
  isAdmin: boolean;
  /** True for admins and moderators (Crew official posts, moderator console) */
  canModerateCrew: boolean;
  hasAccess: boolean;
  accessRequestStatus: 'none' | 'pending' | 'approved' | 'rejected';
  handleLoginSuccess: (loggedInUser: User) => Promise<void>;
  handleLogout: () => void;
  refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [accessRequestStatus, setAccessRequestStatus] = useState<'none' | 'pending' | 'approved' | 'rejected'>('none');

  // Computed values based on role
  const isAdmin = userRole === USER_ROLES.ADMIN;
  const canModerateCrew = isAdmin || userRole === USER_ROLES.MODERATOR;
  const hasAccess = userRole !== null && userRole !== USER_ROLES.PENDING;

  // Function to refresh user role
  const refreshUserRole = useCallback(async () => {
    if (!user) {
      setUserRole(null);
      setAccessRequestStatus('none');
      return;
    }

    try {
      const role = await accessControlService.getUserRole(user.uid);
      setUserRole(role);

      // If pending, check if there's an access request
      if (role === USER_ROLES.PENDING) {
        const request = await accessControlService.getAccessRequest(user.uid);
        if (request) {
          setAccessRequestStatus(request.status as 'pending' | 'approved' | 'rejected');
        } else {
          setAccessRequestStatus('none');
        }
      } else {
        setAccessRequestStatus('none');
      }
    } catch (error) {
      console.error('AuthContext: Error fetching user role:', error);
      // Leave role as PENDING on error — safer than granting access on failure
    }
  }, [user]);

  /** Profile init, role, access request, push, and localStorage sync — used on auth events and after native login. */
  const loadUserDataInBackground = useCallback(async (firebaseUser: User) => {
    try {
      console.log('AuthContext: Loading user data in background...');

      // Register push first: must not depend on profile init or Firestore (can hang/fail on native).
      try {
        console.log('AuthContext: Initializing push notifications (early)...');
        await initializePushNotifications(firebaseUser.uid);
      } catch (pushError) {
        console.error('AuthContext: Error initializing push notifications:', pushError);
      }

      // Initialize user profile in Firebase if needed
      try {
        await firebaseService.initializeUserProfile(firebaseUser);
      } catch (profileError) {
        console.error('AuthContext: Error initializing user profile:', profileError);
        // Non-fatal — role fetch below will still determine access
      }

      // Fetch user role for access control — always runs, always clears isRoleLoading
      try {
        const role = await accessControlService.getUserRole(firebaseUser.uid);
        setUserRole(role);

        // If pending, check access request status
        if (role === USER_ROLES.PENDING) {
          const request = await accessControlService.getAccessRequest(firebaseUser.uid);
          if (request) {
            setAccessRequestStatus(request.status as 'pending' | 'approved' | 'rejected');
          }
        }
        console.log('AuthContext: User role loaded:', role);
      } catch (roleError) {
        console.error('AuthContext: Error loading user role:', roleError);
        // Leave role as PENDING on error — safer than granting access on failure
      } finally {
        setIsRoleLoading(false);
      }

      // Note: getUserAppState is not critical for initial load
      // UserMovieDataContext handles movie data loading
      // This is just for localStorage sync
      const userAppState = await Promise.race([
        firebaseService.getUserAppState(firebaseUser),
        new Promise<null>((resolve) => {
          setTimeout(() => {
            console.warn('AuthContext: getUserAppState timeout after 3 seconds');
            resolve(null);
          }, 3000);
        })
      ]);

      // Sync to localStorage for offline access
      if (userAppState) {
        localStorage.setItem('watchlist', JSON.stringify(userAppState.defaultMovies.watchlist));
        localStorage.setItem('watched', JSON.stringify(userAppState.defaultMovies.watched));
        localStorage.setItem('ratings', JSON.stringify(userAppState.defaultMovies.ratings));

        // Store user watchlists
        localStorage.setItem('userWatchlists', JSON.stringify(userAppState.watchlists));
        console.log('AuthContext: User data synced to localStorage');
      }

      // Clean up stale review drafts (>7 days old)
      try {
        console.log('AuthContext: Cleaning up stale review drafts...');
        const deletedCount = await cleanupStaleDrafts(firebaseUser.uid);
        if (deletedCount > 0) {
          console.log(`AuthContext: Cleaned up ${deletedCount} stale draft(s)`);
        }
      } catch (draftError) {
        console.error('AuthContext: Error cleaning up drafts:', draftError);
        // Non-critical error, don't block user login
      }
    } catch (error) {
      console.error('AuthContext: Error loading user data in background:', error);
      // Safety net: ensure role loading never stays stuck true
      setIsRoleLoading(false);
    }
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let authTimeout: NodeJS.Timeout | null = null;
    let nativeNullAuthDebounceTimer: NodeJS.Timeout | null = null;

    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Initializing Firebase authentication...');
        console.log('AuthContext: Platform is', isCapacitorNative() ? 'native (Capacitor)' : 'web');

        // ============================================
        // NATIVE PLATFORM: Use native Firebase plugins
        // Benefits: Sub-second auth, iOS Keychain persistence
        // ============================================
        if (isCapacitorNative()) {
          console.log('AuthContext: Using native Firebase auth plugin for faster initialization');

          // On native, Firebase is ready immediately (native SDK)
          setIsFirebaseReady(true);

          /** Avoid setUser + dependent effects on duplicate native authStateChange (resume / token / network churn). */
          let lastNotifiedNativeUid: string | null = null;

          // Get current user immediately (stored in iOS Keychain / Android secure storage)
          const currentUser = await nativeFirebase.getCurrentUser();
          console.log('AuthContext: Native current user:', currentUser?.email ?? 'null');

          if (currentUser) {
            // Convert UnifiedUser to a User-like object for compatibility
            // CRITICAL: Must include providerData and metadata - DeleteAccountDialog
            // and firebaseService.isPasswordProvider() access user.providerData.some()
            // which crashes if providerData is undefined
            const userLike = {
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName,
              photoURL: currentUser.photoURL,
              emailVerified: currentUser.emailVerified,
              providerData: [],
              metadata: {
                creationTime: currentUser.creationTime
                  ? new Date(currentUser.creationTime).toUTCString()
                  : undefined,
                lastSignInTime: undefined,
              },
            } as unknown as User;

            lastNotifiedNativeUid = currentUser.uid;
            setIsRoleLoading(true);
            setUser(userLike);
            setIsLoading(false);

            // Load user data in background
            loadUserDataInBackground(userLike);
          } else {
            lastNotifiedNativeUid = null;
            setUser(null);
            setIsLoading(false);
          }

          // Set up native auth state listener for future changes
          // Debounce null events: iOS fires authStateChanged(null) briefly when the app
          // goes to background (e.g. pulling down the notification drawer), immediately
          // followed by authStateChanged(user) on return. Without debouncing, this clears
          // lastNotifiedNativeUid and triggers a full role reload, showing the boot screen.
          unsubscribe = nativeFirebase.onAuthStateChanged((unifiedUser) => {
            console.log('AuthContext: Native auth state changed:', unifiedUser?.email ?? 'null');

            if (unifiedUser) {
              // Cancel any pending null-signout timer — the user is still signed in
              if (nativeNullAuthDebounceTimer) {
                clearTimeout(nativeNullAuthDebounceTimer);
                nativeNullAuthDebounceTimer = null;
                console.log('AuthContext: Cancelled null auth debounce (user returned quickly)');
              }

              if (lastNotifiedNativeUid === unifiedUser.uid) {
                console.log('AuthContext: Ignoring native authStateChange (same uid) to avoid UI/data churn');
                return;
              }
              lastNotifiedNativeUid = unifiedUser.uid;

              const userLike = {
                uid: unifiedUser.uid,
                email: unifiedUser.email,
                displayName: unifiedUser.displayName,
                photoURL: unifiedUser.photoURL,
                emailVerified: unifiedUser.emailVerified,
                providerData: [],
                metadata: {
                  creationTime: unifiedUser.creationTime
                    ? new Date(unifiedUser.creationTime).toUTCString()
                    : undefined,
                  lastSignInTime: undefined,
                },
              } as unknown as User;

              setIsRoleLoading(true);
              setUser(userLike);
              loadUserDataInBackground(userLike);
            } else {
              // Debounce: wait 2s before treating null as a real sign-out.
              // Brief background/foreground cycles (notification drawer, home button) fire
              // null then immediately re-fire the real user — the timer catches that case.
              if (nativeNullAuthDebounceTimer) clearTimeout(nativeNullAuthDebounceTimer);
              nativeNullAuthDebounceTimer = setTimeout(() => {
                nativeNullAuthDebounceTimer = null;
                console.log('AuthContext: Native auth null confirmed after debounce — signing out');
                lastNotifiedNativeUid = null;
                setUser(null);
              }, 2000);
            }
          });

          return;
        }

        // ============================================
        // WEB PLATFORM: Use Firebase JS SDK (existing flow)
        // ============================================
        // Use the shared Firebase initialization
        await initializeFirebase();

        // CRITICAL: Ensure Firestore is fully initialized before setting up auth listener
        // This prevents race conditions where Firestore queries fail with "missing permissions"
        const firebaseReadyTimeout = 5000;
        const isReady = await firebaseManager.waitForReady(firebaseReadyTimeout);
        if (!isReady) {
          console.error('AuthContext: Firebase failed to initialize within', firebaseReadyTimeout, 'ms timeout');
          setIsLoading(false);
          return;
        }

        // Mark Firebase as ready - this will trigger re-renders in contexts
        console.log('AuthContext: Firebase is now ready, setting isFirebaseReady=true');
        setIsFirebaseReady(true);

        // Add safety timeout AFTER Firebase is ready - this gives accurate timing for auth listener
        const authTimeoutMs = getAuthTimeout();
        console.log('AuthContext: Using auth timeout of', authTimeoutMs, 'ms');

        authTimeout = setTimeout(() => {
          console.warn('AuthContext: Auth state listener timed out after', authTimeoutMs, 'ms, forcing loading state to false');
          setIsLoading(false);
        }, authTimeoutMs);

        // Get the auth instance from shared package
        const auth = await getFirebaseAuthAsync();

        console.log('AuthContext: Firebase fully ready (including Firestore), setting up auth state listener...');

        unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          console.log('AuthContext: Auth state changed:', firebaseUser ? `${firebaseUser.displayName} (${firebaseUser.uid})` : 'null');

          // Clear timeout as we got a response
          if (authTimeout) {
            clearTimeout(authTimeout);
            authTimeout = null;
          }

          if (firebaseUser) {
            // Set role loading BEFORE clearing isLoading to avoid a render
            // where both flags are false and userRole is null (would flash dashboard)
            setIsRoleLoading(true);
            setUser(firebaseUser);
            setIsLoading(false);

            // Load user data in background (non-blocking)
            loadUserDataInBackground(firebaseUser);
          } else {
            setUser(firebaseUser);
            setIsLoading(false);
          }
        });

      } catch (error) {
        console.error('AuthContext: Error initializing Firebase:', error);
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (authTimeout) {
        clearTimeout(authTimeout);
      }
      if (nativeNullAuthDebounceTimer) {
        clearTimeout(nativeNullAuthDebounceTimer);
      }
    };
  }, [loadUserDataInBackground]);

  // CRITICAL: handleLoginSuccess now waits for Firebase to be ready before setting the user
  // This prevents race conditions where native auth sets the user before Firebase is initialized
  const handleLoginSuccess = useCallback(async (loggedInUser: User) => {
    console.log('AuthContext: handleLoginSuccess called for', loggedInUser.email);

    // CRITICAL: Set loading state IMMEDIATELY to prevent login page from re-showing
    // This prevents the infinite loop where the login page triggers another login
    setIsLoading(true);

    // ============================================
    // NATIVE PLATFORM: Fast path with native Firebase
    // ============================================
    if (isCapacitorNative()) {
      console.log('AuthContext: handleLoginSuccess using native fast path');
      // On native, Firebase is always ready (native SDK handles everything)
      setIsFirebaseReady(true);
      setIsRoleLoading(true);
      setUser(loggedInUser);
      setIsLoading(false);
      // Native auth listener may not run this pipeline (or may dedupe); always ensure profile + role exist.
      void loadUserDataInBackground(loggedInUser);
      return;
    }

    // ============================================
    // WEB PLATFORM: Existing flow with timeouts
    // ============================================
    // CRITICAL: Add overall timeout to prevent infinite loading
    const overallTimeout = 15000;
    const timeoutId = setTimeout(() => {
      console.error('AuthContext: handleLoginSuccess timed out after', overallTimeout, 'ms - setting user anyway to unblock UI');
      setUser(loggedInUser);
      setIsFirebaseReady(true);
      setIsLoading(false);
    }, overallTimeout);

    try {
      // If Firebase is already ready, set user immediately
      if (isFirebaseInitialized()) {
        console.log('AuthContext: Firebase already initialized, setting user immediately');
        setIsFirebaseReady(true);
        setUser(loggedInUser);
        setIsLoading(false);
        clearTimeout(timeoutId);
        return;
      }

      // CRITICAL: Ensure Firebase initialization is started
      console.log('AuthContext: Firebase not ready, starting initialization...');

      // Add timeout to initializeFirebase to prevent hanging
      try {
        await Promise.race([
          initializeFirebase(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Firebase initialization timeout')), 10000)
          )
        ]);
        console.log('AuthContext: Firebase initialization completed');
      } catch (error) {
        console.error('AuthContext: Failed to initialize Firebase in handleLoginSuccess:', error);
        // Continue anyway - we'll try to set the user without full Firebase ready
      }

      // Wait for Firebase to be fully ready (with timeout)
      const readyTimeout = 5000;
      console.log(`AuthContext: Waiting for Firebase to be ready (timeout: ${readyTimeout}ms)...`);
      const isReady = await firebaseManager.waitForReady(readyTimeout);

      if (isReady) {
        console.log('AuthContext: Firebase now ready, setting user');
        setIsFirebaseReady(true);
        setUser(loggedInUser);
      } else {
        console.error('AuthContext: Firebase initialization timed out, setting user anyway to unblock UI');
        // Set the user anyway so the UI isn't stuck
        // UserMovieDataContext will wait for Firebase before loading data
        setIsFirebaseReady(true); // Set to true to unblock contexts
        setUser(loggedInUser);
      }

      setIsLoading(false);
      clearTimeout(timeoutId);
    } catch (error) {
      console.error('AuthContext: Unexpected error in handleLoginSuccess:', error);
      // Ensure we always set the user and clear loading state
      setUser(loggedInUser);
      setIsFirebaseReady(true);
      setIsLoading(false);
      clearTimeout(timeoutId);
    }
  }, [loadUserDataInBackground]);

  const handleLogout = async () => {
    if (user) {
      try {
        await unregisterPushNotifications(user.uid);
      } catch (error) {
        console.error('AuthContext: Error unregistering push notifications:', error);
      }
    }
    // Sign out of native Firebase (clears iOS Keychain / Android secure storage)
    // Must happen before clearing React state to prevent re-login on next launch
    try {
      await nativeFirebase.signOut();
    } catch (error) {
      console.error('AuthContext: Error signing out of Firebase:', error);
    }
    setUser(null);
    setUserRole(null);
    setIsRoleLoading(false);
    setAccessRequestStatus('none');
    clearPersistedProfiles();
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isRoleLoading,
    isFirebaseReady,
    userRole,
    isAdmin,
    canModerateCrew,
    hasAccess,
    accessRequestStatus,
    handleLoginSuccess,
    handleLogout,
    refreshUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};